import {
  Button, Modal, TextInput,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import React, {
  useCallback, useState, useRef, useEffect, useReducer, Reducer,
} from 'react';
import { useChat } from 'ai/react';
import { FEATURE_MANAGEMENT_USER_ID_KEY } from '@printdeal/feature-management/react';
import { getFromLocalStorage, LS_KEYS } from '../../../helpers/window';
import { ChatMessage, Message } from './ChatMessage';
import { dispatchDataLayerEntry } from '../../../helpers/dataLayerHelpers/productDataLayerHelper';

interface ChatState {
  lastMessageReceived: boolean;
  messageSent: boolean;
  loading: boolean;
  error: boolean;
}

type NewChatState = Partial<ChatState>;
type ChatReducer = Reducer<ChatState, NewChatState>;

const initialState: ChatState = {
  lastMessageReceived: false,
  messageSent: false,
  loading: false,
  error: false,
};

const chatReducer: ChatReducer = (state, newState) => ({
  ...state,
  ...newState,
});

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GuidedSellingModal = ({ isOpen, setIsOpen }: Props) => {
  const intl = useIntl();
  const userId = getFromLocalStorage(FEATURE_MANAGEMENT_USER_ID_KEY);
  const authToken = getFromLocalStorage(LS_KEYS.AUTH_TOKEN);
  const [chat, setChat] = useReducer<ChatReducer>(chatReducer, initialState);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: `assistant-${new Date().getTime()}`,
      role: 'assistant',
      content: intl.formatMessage({
        id: 'guided.selling.modal.chat.welcome',
        defaultMessage: 'Welcome to the guided decision assistance. How can I help you?',
      }),
    },
  ]);

  const chatContainerRef = useRef<HTMLDivElement>(null);

  const { handleSubmit, handleInputChange, input } = useChat({
    api: `${process.env.GATSBY_PRODUCT_AI_ENDPOINT}/guided-selling-chat`,
    id: userId,
    headers: {
      Authorization: authToken as string,
    },
    body: {
      userId,
    },
    onResponse: async (response) => {
      if (!response.ok) {
        setChat({ error: true, loading: false });
        setMessages((prev) => [...prev, {
          id: `assistant-${new Date().getTime()}`,
          role: 'assistant',
          content: intl.formatMessage({
            id: 'guided.selling.modal.chat.error',
            defaultMessage: 'I am sorry, I could not process your request. Please try again later.',
          }),
        }]);
        return;
      }

      const body = await response.json() as { messages: Message[] };
      setMessages((prev) => [...prev, ...body.messages]);
      setChat({ loading: false, lastMessageReceived: true, messageSent: false });

      // Dispatch dataLayer event
      dispatchDataLayerEntry(
        'receive_chat_message',
        'display',
        {
          experiment: 'EXP1530',
        },
      );
    },
  });

  const handleSendMessage = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setChat({ messageSent: true, error: false, lastMessageReceived: false });
    setMessages((prev) => [...prev, {
      id: `user-${new Date().getTime()}`,
      role: 'user',
      content: input,
    }]);

    // Call the handleSubmit function from the useChat hook
    handleSubmit(e);

    // Dispatch dataLayer event
    dispatchDataLayerEntry(
      'send_chat_message',
      'click',
      {
        experiment: 'EXP1530',
        message: input,
      },
    );
  }, [input, handleSubmit]);

  useEffect(() => {
    // setLoading after 1500ms, so it looks like the assistant starts typing
    const timeout = setTimeout(() => {
      if (!chat.error && !chat.lastMessageReceived && chat.messageSent) {
        setChat({ loading: true });
      }
    }, 1500);

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeout);
  }, [chat.error, chat.lastMessageReceived, chat.messageSent]);

  useEffect(() => {
    if (chatContainerRef.current) {
      // Scroll to the bottom of the chat container when new messages are added
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, chat.loading]);

  return (
    <Modal
      size="lg"
      title={intl.formatMessage({ id: 'general.chooseProduct.decisionAssistance.modal.title', defaultMessage: 'Guided Decision Assistance' })}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      dismissLabel={intl.formatMessage({ id: 'general.alert.dismiss', defaultMessage: 'Dismiss' })}
    >
      <Modal.Content>
        <div className="pd-flex pd-max-h-[600px] pd-min-h-96 pd-flex-col pd-gap-4 pd-overflow-y-auto pd-px-2 pd-pb-2" ref={chatContainerRef}>
          {messages.map((message) => (<ChatMessage key={message.id} message={message} />))}
          {chat.loading && (
          <div className="pd-flex pd-items-center pd-gap-2">
            <span className="pd-animate-pulse pd-font-semibold pd-italic pd-text-dim-800">
              <FormattedMessage id="guided.selling.modal.chat.typing" defaultMessage="Assistant is typing..." />
            </span>
          </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer className="pd-pb-1">
        <form onSubmit={handleSendMessage} className="pd-flex pd-w-full pd-gap-4">
          <TextInput
            value={input}
            onChange={handleInputChange}
            placeholder={intl.formatMessage({ id: 'guided.selling.modal.chat.inputPlaceholder', defaultMessage: 'Type your message here...' })}
            className="pd-w-full"
          />
          <Button type="submit" variant="primary" className="pd-w-1/4">
            <FormattedMessage id="guided.selling.modal.chat.send" defaultMessage="Send" />
          </Button>
        </form>
        <p className="pd-ml-1 pd-py-2 pd-text-xs pd-text-dim-500">
          <FormattedMessage
            id="guided.selling.modal.chat.disclaimer"
            defaultMessage="I’m powered by AI, so surprises and mistakes are possible. Make sure you verify the information I provide."
          />
        </p>
      </Modal.Footer>
    </Modal>
  );
};
